import React, { useMemo, type ComponentProps } from 'react';
import { createColor, hexAlpha, isDefined, modifyColor, RESERVATION_SEAT_TYPE } from '@nfctron/nexus-core';
import { cn, type BasicComponent } from '@nfctron/nexus-react';

import { type SeatTypes as Types } from './Seat.types';

/**
 * Seat component for type CIRCLE_50CM
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const SeatCircle50: BasicComponent<Types.Circle50Props> = (props) => {
	const {
		seat,
		seatProps: {
			seatIs: { selected, unavailable, saleable, soldOut, carted },
			seatContent,
			svgProps,
		},
		...rest
	} = props;

	/** seat type 1 fixed radius of 250px ~ 25cm */
	const RADIUS = 250;
	const TEXT_SIZE = RADIUS * 0.65;
	const ICON_SIZE = RADIUS * 0.75;

	/** circle override params */
	const overrideParams = useMemo(() => {
		/** base seat params */
		let params: ComponentProps<'circle'> = {
			fill: seat.category.color,
			stroke: 'none',
			strokeWidth: 0,
		};

		/** selected seat */
		if (selected) {
			return { ...params, ...svgProps, fill: modifyColor(hexAlpha(svgProps?.fill ?? seat.category.color), (c) => c.darken(0.45)) };
		}

		/** unavailable sold out seat */
		if (soldOut) {
			params.fill = '#d4d4d8';
			params.strokeWidth = 10;
			params.stroke = '#d4d4d8';
			return { ...params, ...svgProps };
		}

		/** unavailable not for sale seat */
		if (!saleable) {
			params.fill = '#d4d4d8';
			params.strokeWidth = 10;
			params.stroke = '#d4d4d8';
			return { ...params, ...svgProps };
		}

		/** carted seat */
		if (carted) {
			params.fill = '#FFFFFF00';
			params.stroke = seat.category.color;
			params.strokeWidth = RADIUS / 10;
			return { ...params, ...svgProps };
		}

		return { ...params, ...svgProps };
	}, [seat.category.color, selected, soldOut, saleable, carted, svgProps]);

	/** seat inner children content */
	const seatContentNode = useMemo(() => {
		/** get seat content to display */
		const toDisplay = isDefined(seatContent) ? seatContent : carted ? 'checkIcon' : 'text';

		switch (toDisplay) {
			/** seat place label */
			case 'text': {
				return (
					<text
						x={seat.position.x}
						y={seat.position.y}
						fontSize={TEXT_SIZE}
						dominantBaseline="middle"
						textAnchor="middle"
						fill="currentColor"
						fontWeight={!unavailable ? 'bold' : 'normal'}
					>
						{seat.label}
					</text>
				);
			}
			/** check icon */
			case 'checkIcon': {
				return (
					<use xlinkHref="#check-icon" width={ICON_SIZE} height={ICON_SIZE} x={seat.position.x - ICON_SIZE / 2} y={seat.position.y - ICON_SIZE / 2} />
				);
			}
			/** close icon */
			case 'closeIcon': {
				return (
					<use xlinkHref="#close-icon" width={ICON_SIZE} height={ICON_SIZE} x={seat.position.x - ICON_SIZE / 2} y={seat.position.y - ICON_SIZE / 2} />
				);
			}
			/** watch icon */
			case 'watchIcon': {
				return (
					<use xlinkHref="#watch-icon" width={ICON_SIZE} height={ICON_SIZE} x={seat.position.x - ICON_SIZE / 2} y={seat.position.y - ICON_SIZE / 2} />
				);
			}
			/** star icon */
			case 'starIcon': {
				return (
					<use xlinkHref="#star-icon" width={ICON_SIZE} height={ICON_SIZE} x={seat.position.x - ICON_SIZE / 2} y={seat.position.y - ICON_SIZE / 2} />
				);
			}
			/** nothing to display */
			default: {
				return null;
			}
		}
	}, [ICON_SIZE, TEXT_SIZE, carted, seat.label, seat.position.x, seat.position.y, seatContent, unavailable]);

	return (
		<g>
			{/* seat svg circle */}
			<circle
				r={RADIUS}
				cx={seat.position.x}
				cy={seat.position.y}
				rotate={seat.position.rotation}
				className={cn(['transition-all cursor-pointer', !selected && 'hover:opacity-90'])}
				{...overrideParams}
				{...rest}
			/>
			<g
				style={{
					pointerEvents: 'none',
					touchAction: 'none',
					color: modifyColor(hexAlpha(overrideParams.fill ?? seat.category.color), (c) => {
						let color = createColor('#000');
						if (c.isDark()) color = createColor('#FFF');
						return color.fade(unavailable ? 0.75 : 0);
					}),
				}}
			>
				{seatContentNode}
			</g>
		</g>
	);
};

/**
 * Seat component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const Seat: BasicComponent<Types.Props> = (props) => {
	const { seat, onSeatSelect, onSeatUnselect, getSeatProps, ...rest } = props;

	/** rendered seat node */
	const seatNode = useMemo(() => {
		if (!isDefined(seat)) return null;
		const seatProps = getSeatProps(seat);

		/** click handler */
		const onClick = () => {
			if (seatProps.seatIs.selected) return onSeatUnselect(seat.seatId);
			return onSeatSelect(seat.seatId);
		};

		switch (seat.seatTypeId) {
			case RESERVATION_SEAT_TYPE.CIRCLE_50CM:
				return <SeatCircle50 seatProps={seatProps} seat={seat} onClick={onClick} {...rest} />;
			// TODO: support other seat types
			default:
				return null;
		}
	}, [getSeatProps, onSeatSelect, onSeatUnselect, rest, seat]);

	/** unable to render the seat due to missing config */
	if (!isDefined(seatNode)) {
		console.debug(`[${Seat.displayName}] Unable to render seat "${rest.id}" due to missing config`);
		return null;
	}

	return seatNode;
};

/** Seat component display name */
Seat.displayName = 'component.Seat';
/** SeatCircle50 component display name */
SeatCircle50.displayName = 'component.SeatCircle50';
